import type { ReactNode } from 'react';
import { alignerMonitoringState } from '../components/alignerMonitoringState';
import { Tooltip } from '../components/Tooltip';

interface MonitoringStatusPillProps {
    status: string | null | undefined;
}

interface MonitoringStatusTone {
    label: string;
    className: string;
    titleClassName: string;
}

const ACTIVE_TONE: MonitoringStatusTone = {
    label: 'Active',
    className: 'bg-green-50 text-green-700',
    titleClassName: 'text-green-700'
};

const MONITORING_STATUS_TONES: Record<string, MonitoringStatusTone> = {
    // 'resumed' is Monday-speak for an active monitoring (v1 resume action /
    // Monday->DB sync): treat it exactly like started.
    started: ACTIVE_TONE,
    resumed: ACTIVE_TONE,
    paused: {
        label: 'Paused',
        className: 'bg-yellow-50 text-yellow-700',
        titleClassName: 'text-amber-600'
    },
    stopped: {
        label: 'Stopped',
        className: 'bg-gray-100 text-gray-500',
        titleClassName: 'text-red-600'
    }
};

const MONITORING_STATUS_DEFAULT_TONE: MonitoringStatusTone = {
    label: 'Not started',
    className: 'bg-gray-100 text-gray-500',
    titleClassName: 'text-gray-800'
};

// The local mirror stores DM's status capitalized, hence the lowercasing.
function monitoringStatusTone(status: string | null | undefined): MonitoringStatusTone {
    return MONITORING_STATUS_TONES[(status ?? '').toLowerCase()] ?? MONITORING_STATUS_DEFAULT_TONE;
}

function MonitoringStatusPill({ status }: MonitoringStatusPillProps) {
    const tone = monitoringStatusTone(status);
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold ${tone.className}`}
        >
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
            Monitoring · {tone.label}
        </span>
    );
}

interface MonitoringStatusTitleProps {
    status: string | null;
    current: number | null;
    total: number | null;
    children: ReactNode;
}

/** DM column status icon and tooltip, with status-coloured text, for a header title. */
function MonitoringStatusTitle({ status, current, total, children }: MonitoringStatusTitleProps) {
    const tone = monitoringStatusTone(status);
    const { meta, tooltip } = alignerMonitoringState(current, total, status);
    const { Icon } = meta;
    return (
        <Tooltip content={tooltip} side="top">
            <span className={tone.titleClassName}>
                <Icon
                    aria-hidden="true"
                    className={`mr-1 inline-block h-4 w-4 align-text-bottom ${meta.iconColor}`}
                />
                <span className="sr-only">{`Monitoring ${tone.label}: `}</span>
                {children}
            </span>
        </Tooltip>
    );
}

interface AppActivationPillProps {
    status: string | null | undefined;
}

const DM_APP_ACTIVATED_STATUS = 'Patient App Activated';

function AppActivationPill({ status }: AppActivationPillProps) {
    const isActivated = status === DM_APP_ACTIVATED_STATUS;
    const className = isActivated ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-800';
    const label = isActivated ? 'Activated' : 'Not activated';
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold ${className}`}
        >
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
            App · {label}
        </span>
    );
}

export { MonitoringStatusPill, MonitoringStatusTitle, AppActivationPill };
